import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import AdaptationHelp from "../adaptation/AdaptationHelp";
import { DEPARTMENTS } from "../config/api";
import { deleteDashboardView, getDashboard, getDashboardViews, saveDashboardView } from "../services/adaptationBuilderApi";

/**
 * Дашборд адаптации — настраиваемые виджеты и сохранённые виды. [Claude] 2026-11-02, Чат 3, №7.
 */

const WIDGET_TYPES = {
    summary: "Сводка",
    matrix: "Сотрудники × этапы",
    dynamics: "Динамика сквозного вопроса",
    questions: "Средние по вопросам",
    pending: "Кто не заполнил",
};

const DEFAULT_CONFIG = {
    filters: { dateFrom: "", dateTo: "", department: "" },
    widgets: [
        { id: "w1", type: "summary", title: "Сводка", width: "full", params: {} },
        { id: "w2", type: "matrix", title: "Сотрудники × этапы", width: "full", params: { colorBy: "risk" } },
        { id: "w3", type: "dynamics", title: "Динамика", width: "half", params: { question: "", groupBy: "department", valueMode: "pct" } },
        { id: "w4", type: "pending", title: "Кто не заполнил", width: "half", params: {} },
    ],
};

const RISK_STYLE = {
    low: "bg-emerald-100 text-emerald-800",
    medium: "bg-amber-100 text-amber-800",
    high: "bg-red-100 text-red-700",
    critical: "bg-red-600 text-white",
    uncalculated: "bg-slate-100 text-slate-500",
};
const STATUS_STYLE = {
    planned: "bg-slate-100 text-slate-500",
    collecting: "bg-sky-100 text-sky-800",
    overdue: "bg-red-100 text-red-700",
    data_collected: "bg-indigo-100 text-indigo-800",
    draft_ready: "bg-violet-100 text-violet-800",
    completed: "bg-emerald-100 text-emerald-800",
    forced_completed: "bg-slate-200 text-slate-700",
};
const PALETTE = ["#0f172a", "#f59e0b", "#0ea5e9", "#10b981", "#ef4444", "#8b5cf6", "#64748b", "#ec4899", "#14b8a6", "#a16207"];

const input = "h-8 rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus:border-amber-400";
const btn = "inline-flex h-8 items-center rounded-md px-2.5 text-sm font-semibold transition disabled:opacity-50";
const btnGhost = `${btn} border border-slate-200 bg-white text-slate-700 hover:border-slate-400`;

let widgetSeq = 100;
const newId = () => `w${Date.now()}${widgetSeq++}`;

function SummaryWidget({ rows }) {
    const people = new Set(rows.map((r) => r.enrollment_id)).size;
    const count = (key, value) => rows.filter((r) => r[key] === value).length;
    const tiles = [
        ["Сотрудников", people],
        ["Этапов", rows.length],
        ["Сбор ответов", count("status", "collecting")],
        ["Просрочено", count("status", "overdue")],
        ["Завершено", count("status", "completed") + count("status", "forced_completed")],
        ["Высокий риск", count("risk", "high") + count("risk", "critical")],
        ["Средний риск", count("risk", "medium")],
        ["Просят разговор с HR", rows.filter((r) => r.talk_hr).length],
    ];
    return (
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 xl:grid-cols-8">
            {tiles.map(([label, value]) => (
                <div key={label} className="rounded-md bg-slate-50 px-2.5 py-1">
                    <p className="truncate text-[11px] text-slate-500">{label}</p>
                    <p className="text-lg font-bold leading-6 text-slate-900">{value}</p>
                </div>
            ))}
        </div>
    );
}

function MatrixWidget({ rows, params }) {
    const navigate = useNavigate();
    const colorBy = params.colorBy || "risk";
    const { people, stages, cells } = useMemo(() => {
        const stageFirst = new Map();
        const peopleMap = new Map();
        const cellMap = new Map();
        rows.forEach((r) => {
            const label = r.kind_label || r.kind;
            const date = String(r.plan_date || "");
            if (!stageFirst.has(label) || stageFirst.get(label) > date) stageFirst.set(label, date);
            if (!peopleMap.has(r.enrollment_id)) peopleMap.set(r.enrollment_id, r);
            const key = `${r.enrollment_id}|${label}`;
            cellMap.set(key, [...(cellMap.get(key) || []), r]);
        });
        return {
            people: [...peopleMap.values()].sort((a, b) => a.full_name.localeCompare(b.full_name, "ru")),
            stages: [...stageFirst.entries()].sort((a, b) => (a[1] < b[1] ? -1 : 1)).map(([label]) => label),
            cells: cellMap,
        };
    }, [rows]);
    if (!people.length) return <p className="text-sm text-slate-500">Нет данных за период</p>;
    return (
        <div className="max-h-[calc(100vh-15rem)] overflow-auto">
            <table className="min-w-full text-xs">
                <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                        <th className="sticky left-0 z-10 bg-white px-2 py-1 text-left font-semibold text-slate-500">Сотрудник</th>
                        {stages.map((s) => <th key={s} className="whitespace-nowrap px-1.5 py-1 text-left font-semibold text-slate-500">{s}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {people.map((p) => (
                        <tr key={p.enrollment_id} className="border-t border-slate-100">
                            <td className="sticky left-0 bg-white px-2 py-0.5">
                                <Link to={`/tests/adaptation/case/${p.enrollment_id}`} className="font-semibold text-slate-900 hover:underline">{p.full_name}</Link>
                                <span className="ml-1.5 text-[11px] text-slate-400">{p.department}</span>
                            </td>
                            {stages.map((s) => {
                                const list = cells.get(`${p.enrollment_id}|${s}`) || [];
                                return (
                                    <td key={s} className="px-1 py-0.5">
                                        <div className="flex flex-wrap gap-1">
                                            {list.map((cell) => (
                                                <button
                                                    key={cell.id}
                                                    type="button"
                                                    title={`${cell.status_label} · риск: ${cell.risk_label} · ${cell.plan_date}`}
                                                    onClick={() => navigate(`/tests/adaptation/${cell.id}`)}
                                                    className={`rounded px-1.5 py-0.5 font-semibold ${(colorBy === "risk" ? RISK_STYLE[cell.risk] : STATUS_STYLE[cell.status]) || "bg-slate-100"}`}
                                                >
                                                    {colorBy === "risk" ? (cell.risk_label || "—") : (cell.status_label || "—")}
                                                </button>
                                            ))}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function DynamicsWidget({ points, questions, rows, params }) {
    const qid = params.question || (questions.find((q) => q.cross) || questions[0] || {}).id;
    const question = questions.find((q) => q.id === qid);
    const groupBy = params.groupBy || "department";
    const valueMode = params.valueMode || "pct";
    const names = useMemo(() => new Map(rows.map((r) => [r.enrollment_id, r.full_name])), [rows]);
    const { data, series } = useMemo(() => {
        const byPerson = new Map();
        points.filter((p) => p.q === qid).sort((a, b) => (a.date < b.date ? -1 : 1)).forEach((p) => {
            const list = byPerson.get(p.enrollment_id) || [];
            list.push(p);
            byPerson.set(p.enrollment_id, list);
        });
        const value = (p) => (valueMode === "pct" ? p.pct : Number(p.value === "да" ? 1 : p.value === "нет" ? 0 : p.value));
        const maxLen = Math.max(0, ...[...byPerson.values()].map((l) => l.length));
        const groups = new Map();
        byPerson.forEach((list, enrollmentId) => {
            const key = groupBy === "employee" ? (names.get(enrollmentId) || `#${enrollmentId}`) : groupBy === "department" ? (list[0].department || "—") : "Все";
            const g = groups.get(key) || Array.from({ length: maxLen }, () => []);
            list.forEach((p, i) => { const v = value(p); if (v != null && Number.isFinite(v)) g[i].push(v); });
            groups.set(key, g);
        });
        const keys = [...groups.keys()].slice(0, groupBy === "employee" ? Number(params.limit || 10) : 20);
        const rowsOut = Array.from({ length: maxLen }, (_, i) => {
            const row = { step: `Замер ${i + 1}` };
            keys.forEach((k) => {
                const vals = groups.get(k)[i];
                if (vals && vals.length) row[k] = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
            });
            return row;
        });
        return { data: rowsOut, series: keys };
    }, [points, qid, groupBy, valueMode, names, params.limit]);
    if (!question) return <p className="text-sm text-slate-500">Нет ответов на шкальные вопросы за период</p>;
    return (
        <div>
            <p className="mb-2 text-xs text-slate-500">{question.text}{valueMode === "pct" ? " · в % от шкалы" : ""}</p>
            {data.length ? (
                <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ left: -16, right: 8, top: 4, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="step" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} domain={valueMode === "pct" ? [0, 100] : ["auto", "auto"]} />
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {series.map((s, i) => <Line key={s} type="monotone" dataKey={s} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />)}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            ) : <p className="text-sm text-slate-500">Нет данных</p>}
        </div>
    );
}

function QuestionsWidget({ points, questions, params }) {
    const data = useMemo(() => {
        const list = questions.filter((q) => (!params.crossOnly || q.cross) && (!params.role || q.role === params.role));
        return list.map((q) => {
            const vals = points.filter((p) => p.q === q.id && p.pct != null).map((p) => p.pct);
            return { name: q.text.length > 40 ? `${q.text.slice(0, 40)}…` : q.text, full: q.text, avg: vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null, n: vals.length };
        }).filter((d) => d.avg != null).sort((a, b) => a.avg - b.avg).slice(0, Number(params.limit || 15));
    }, [points, questions, params.crossOnly, params.role, params.limit]);
    if (!data.length) return <p className="text-sm text-slate-500">Нет данных</p>;
    return (
        <div style={{ height: Math.max(160, data.length * 28 + 40) }}>
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={220} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, _n, item) => [`${v}% (ответов: ${item.payload.n})`, item.payload.full]} />
                    <Bar dataKey="avg" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function PendingWidget({ rows }) {
    const list = rows.filter((r) => ["collecting", "overdue"].includes(r.status))
        .map((r) => ({ ...r, waiting: (r.progress || []).filter((p) => p.state === "waiting").map((p) => p.label) }))
        .filter((r) => r.waiting.length)
        .sort((a, b) => (a.status === "overdue" ? -1 : 1) - (b.status === "overdue" ? -1 : 1) || String(a.plan_date).localeCompare(String(b.plan_date)));
    if (!list.length) return <p className="text-sm text-emerald-700">Все анкеты по текущим этапам заполнены</p>;
    return (
        <div className="max-h-[calc(100vh-24rem)] min-h-40 overflow-y-auto">
            <table className="w-full text-sm">
                <tbody>
                    {list.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100">
                            <td className="py-1 pr-2"><Link className="font-medium hover:underline" to={`/tests/adaptation/${r.id}`}>{r.full_name}</Link><span className="block text-xs text-slate-400">{r.kind_label}</span></td>
                            <td className="py-1 pr-2 text-xs">{new Date(r.plan_date).toLocaleDateString("ru-RU")}</td>
                            <td className="py-1 pr-2 text-xs">ждём: <b>{r.waiting.join(", ")}</b></td>
                            <td className="py-1 text-right"><span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status]}`}>{r.status_label}</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function WidgetSettings({ widget, questions, onChange }) {
    const p = widget.params || {};
    const set = (patch) => onChange({ ...widget, params: { ...p, ...patch } });
    return (
        <div className="mb-2 flex flex-wrap items-center gap-1.5 rounded-md bg-slate-50 p-1.5 text-xs">
            <input className={`${input} w-48`} value={widget.title} onChange={(e) => onChange({ ...widget, title: e.target.value })} />
            <select className={input} value={widget.width} onChange={(e) => onChange({ ...widget, width: e.target.value })}>
                <option value="half">половина ширины</option>
                <option value="full">вся ширина</option>
            </select>
            {widget.type === "matrix" ? (
                <select className={input} value={p.colorBy || "risk"} onChange={(e) => set({ colorBy: e.target.value })}>
                    <option value="risk">цвет — риск</option>
                    <option value="status">цвет — статус</option>
                </select>
            ) : null}
            {widget.type === "dynamics" ? (
                <>
                    <select className={`${input} max-w-xs`} value={p.question || ""} onChange={(e) => set({ question: e.target.value })}>
                        <option value="">— сквозной вопрос —</option>
                        {questions.map((q) => <option key={q.id} value={q.id}>{q.cross ? "◆ " : ""}{q.text.slice(0, 80)}</option>)}
                    </select>
                    <select className={input} value={p.groupBy || "department"} onChange={(e) => set({ groupBy: e.target.value })}>
                        <option value="department">по отделам</option>
                        <option value="employee">по сотрудникам</option>
                        <option value="all">все вместе</option>
                    </select>
                    <select className={input} value={p.valueMode || "pct"} onChange={(e) => set({ valueMode: e.target.value })}>
                        <option value="pct">в % от шкалы</option>
                        <option value="raw">в баллах</option>
                    </select>
                    {p.groupBy === "employee" ? <input type="number" className={`${input} w-20`} value={p.limit || 10} onChange={(e) => set({ limit: e.target.value })} title="сколько сотрудников" /> : null}
                </>
            ) : null}
            {widget.type === "questions" ? (
                <>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={Boolean(p.crossOnly)} onChange={(e) => set({ crossOnly: e.target.checked })} /> только сквозные</label>
                    <select className={input} value={p.role || ""} onChange={(e) => set({ role: e.target.value })}>
                        <option value="">все участники</option>
                        <option value="employee">сотрудник</option>
                        <option value="manager">руководитель</option>
                        <option value="hr">HR</option>
                    </select>
                    <input type="number" className={`${input} w-20`} value={p.limit || 15} onChange={(e) => set({ limit: e.target.value })} title="сколько вопросов" />
                </>
            ) : null}
        </div>
    );
}

export default function AdaptationDashboardPage() {
    const { showAlert } = useAlertContext();
    const [views, setViews] = useState([]);
    const [viewId, setViewId] = useState("");
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [data, setData] = useState({ rows: [], points: [], questions: [] });
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [help, setHelp] = useState(false);

    useEffect(() => {
        getDashboardViews().then((list) => {
            setViews(list || []);
            const def = (list || []).find((v) => v.is_default);
            if (def) { setViewId(String(def.id)); setConfig({ ...DEFAULT_CONFIG, ...def.config }); }
        }).catch(() => setViews([]));
    }, []);

    const filters = config.filters || {};
    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(await getDashboard({ dateFrom: filters.dateFrom, dateTo: filters.dateTo, department: filters.department }));
        } catch (e) {
            showAlert(e?.message || "Не удалось загрузить дашборд", "error");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.dateFrom, filters.dateTo, filters.department]);
    useEffect(() => { load(); }, [load]);

    const setFilters = (patch) => setConfig((c) => ({ ...c, filters: { ...(c.filters || {}), ...patch } }));
    const updateWidget = (w) => setConfig((c) => ({ ...c, widgets: c.widgets.map((x) => (x.id === w.id ? w : x)) }));
    const moveWidget = (id, delta) => setConfig((c) => {
        const list = [...c.widgets];
        const i = list.findIndex((w) => w.id === id);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= list.length) return c;
        [list[i], list[j]] = [list[j], list[i]];
        return { ...c, widgets: list };
    });
    const addWidget = (type) => {
        if (!type) return;
        const w = { id: newId(), type, title: WIDGET_TYPES[type], width: type === "summary" || type === "matrix" ? "full" : "half", params: {} };
        setConfig((c) => ({ ...c, widgets: [...c.widgets, w] }));
        setEditing(w.id);
    };
    const saveView = async (asNew) => {
        const current = views.find((v) => String(v.id) === viewId);
        const name = asNew || !current ? window.prompt("Название вида", current?.name || "Мой вид") : current.name;
        if (!name) return;
        try {
            const saved = await saveDashboardView({ id: asNew ? undefined : current?.id, name, config, is_default: current?.is_default });
            const list = await getDashboardViews();
            setViews(list || []);
            setViewId(String(saved.id));
            showAlert("Вид сохранён", "success");
        } catch (e) {
            showAlert(e?.message || "Не удалось сохранить вид", "error");
        }
    };
    const makeDefault = async () => {
        const current = views.find((v) => String(v.id) === viewId);
        if (!current) return;
        await saveDashboardView({ id: current.id, name: current.name, config, is_default: true });
        setViews(await getDashboardViews());
        showAlert("Вид открывается по умолчанию", "success");
    };
    const removeView = async () => {
        const current = views.find((v) => String(v.id) === viewId);
        if (!current || !window.confirm(`Удалить вид «${current.name}»?`)) return;
        await deleteDashboardView(current.id);
        setViews(await getDashboardViews());
        setViewId("");
        setConfig(DEFAULT_CONFIG);
    };

    const scaleQuestions = data.questions || [];
    const renderWidget = (w) => {
        const p = w.params || {};
        if (w.type === "summary") return <SummaryWidget rows={data.rows} />;
        if (w.type === "matrix") return <MatrixWidget rows={data.rows} params={p} />;
        if (w.type === "dynamics") return <DynamicsWidget rows={data.rows} points={data.points} questions={scaleQuestions} params={p} />;
        if (w.type === "questions") return <QuestionsWidget points={data.points} questions={scaleQuestions} params={p} />;
        if (w.type === "pending") return <PendingWidget rows={data.rows} />;
        return null;
    };

    return (
        <MainLayout className="!px-3 !py-2 bg-slate-50/70">
            <AdaptationHelp open={help} onClose={() => setHelp(false)} section="dashboard" />
            <div className="sticky top-12 z-20 -mx-1 mb-2 flex flex-wrap items-center gap-1.5 bg-slate-50/95 px-1 py-1 backdrop-blur">
                <Link to="/tests/adaptation" className="text-sm text-slate-500 hover:text-slate-900">← Адаптация</Link>
                <h1 className="mr-2 text-lg font-bold text-slate-900">Дашборд адаптации</h1>
                <select className={input} value={viewId} onChange={(e) => {
                    setViewId(e.target.value);
                    const v = views.find((x) => String(x.id) === e.target.value);
                    setConfig(v ? { ...DEFAULT_CONFIG, ...v.config } : DEFAULT_CONFIG);
                }}>
                    <option value="">Стандартный вид</option>
                    {views.map((v) => <option key={v.id} value={v.id}>{v.name}{v.is_default ? " ★" : ""}</option>)}
                </select>
                <button type="button" className={btnGhost} onClick={() => saveView(false)}>Сохранить</button>
                <button type="button" className={btnGhost} onClick={() => saveView(true)}>Сохранить как…</button>
                {viewId ? <button type="button" className={btnGhost} onClick={makeDefault}>По умолчанию</button> : null}
                {viewId ? <button type="button" className={`${btnGhost} text-red-600`} onClick={removeView}>Удалить</button> : null}
                <span className="mx-1 h-6 w-px bg-slate-200" />
                <input type="date" className={input} value={filters.dateFrom || ""} onChange={(e) => setFilters({ dateFrom: e.target.value })} title="этапы с" />
                <input type="date" className={input} value={filters.dateTo || ""} onChange={(e) => setFilters({ dateTo: e.target.value })} title="этапы по" />
                <select className={input} value={filters.department || ""} onChange={(e) => setFilters({ department: e.target.value })}>
                    <option value="">Все отделы</option>
                    {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className={input} value="" onChange={(e) => addWidget(e.target.value)}>
                    <option value="">+ виджет</option>
                    {Object.entries(WIDGET_TYPES).map(([type, label]) => <option key={type} value={type}>{label}</option>)}
                </select>
                <Link to="/tests/adaptation/builder" className={btnGhost}>Конструктор</Link>
                <button type="button" className={btnGhost} onClick={() => setHelp(true)}>? Справка</button>
                {loading ? <span className="text-xs text-slate-400">обновляю…</span> : null}
            </div>
            <div className="grid gap-2 lg:grid-cols-2">
                {config.widgets.map((w) => (
                    <section key={w.id} className={`rounded-lg border border-slate-200 bg-white p-2 ${w.width === "full" ? "lg:col-span-2" : ""}`}>
                        <div className="mb-1.5 flex items-center justify-between gap-2">
                            <h2 className="text-xs font-bold uppercase tracking-wide text-slate-600">{w.title}</h2>
                            <div className="flex items-center gap-1 text-slate-400">
                                <button type="button" className="px-1 hover:text-slate-900" title="Выше" onClick={() => moveWidget(w.id, -1)}>↑</button>
                                <button type="button" className="px-1 hover:text-slate-900" title="Ниже" onClick={() => moveWidget(w.id, 1)}>↓</button>
                                <button type="button" className="px-1 hover:text-slate-900" title="Настроить" onClick={() => setEditing(editing === w.id ? null : w.id)}>⚙</button>
                                <button type="button" className="px-1 hover:text-red-600" title="Убрать" onClick={() => setConfig((c) => ({ ...c, widgets: c.widgets.filter((x) => x.id !== w.id) }))}>×</button>
                            </div>
                        </div>
                        {editing === w.id ? <WidgetSettings widget={w} questions={scaleQuestions} onChange={updateWidget} /> : null}
                        {renderWidget(w)}
                    </section>
                ))}
            </div>
            {!config.widgets.length ? <p className="text-sm text-slate-500">Добавьте виджеты через «+ виджет».</p> : null}
        </MainLayout>
    );
}
